import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms'
import { TypedFormGroup } from '@feature/forms/form-model';;
import { MatFormFieldControl } from '@angular/material/form-field';
import { FileObject, FileObjectStatus } from '@core/models/file-object.model';
import { timer, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

export type FileInputState = FileObjectStatus | 'deleting' | 'empty' | 'not-uploaded' | 'uploading';

function createCounter(seconds: number, killer: Subject<null>): Observable<number> {
  return timer(0, 1000).pipe(
    map((n) => seconds - n),
    take(seconds + 1),
    takeUntil(killer),
  );
}

@Component({
    selector: 'wai-form-file-input',
    templateUrl: './form-file-input.component.html',
    styleUrls: ['./form-file-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: MatFormFieldControl, useExisting: FormFileInputComponent }],
    standalone: false
})
export class FormFileInputComponent implements ControlValueAccessor, MatFormFieldControl<FileObject>, OnDestroy {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() forcedDisabled = false;
  public get icon(): string {
    if (this.state === 'empty') {
      return 'cloud_upload';
    }

    return 'attachment';
  }

  public get empty(): boolean {
    return !this.value;
  }

  public get shouldLabelFloat(): boolean {
    return true;
  }

  public get placeholder(): string {
    return null;
  }

  @Input()
  public get required(): boolean {
    return this._required;
  }

  public set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    // Override disabled state if forceEnabled is true (for client onboarding)
    if (this.forceEnabled) {
      this._disabled = false;
    } else {
      this._disabled = coerceBooleanProperty(value);
    }
    this.stateChanges.next();
  }

  @Input()
  public get value(): FileObject | null {
    if (this._value) {
      return this._value;
    }

    return null;
  }

  public set value(file: FileObject | null) {
    if (file && !file.status) {
      this.state = 'not-uploaded';
    }
    this._value = file;
    this.stateChanges.next();
  }

  public get errorState(): boolean {
    return false;
  }

  @Input()
  public get state(): FileInputState {
    return this._state;
  }

  public set state(state: FileInputState) {
    this._state = state;
    this.stateChanges.next();
  }

  public static nextId = 0;

  public counterKiller = new Subject<null>();

  @Input() public accept = '*';

  @Input() public deleteCountdown: number | null = null;

  /**
   * Force enable uploads regardless of form state.
   * Used in client onboarding where uploads should always be enabled.
   */
  @Input() public forceEnabled: boolean = false;

  @Output() public fileChange: EventEmitter<FileObject | null> = new EventEmitter<FileObject | null>(null);

  @Output() public fileOpen: EventEmitter<FileObject> = new EventEmitter<FileObject>();

  @HostBinding('class.wai-form-file-input') public className = true;

  public counter = 0;
  public stateChanges = new Subject<void>();
  public focused = false;
  public controlType = 'form-file-input';
  public id = `${this.controlType}-${FormFileInputComponent.nextId++}`;
  public onChange: (value: FileObject) => void;
  public onTouched: (value: unknown) => void;

  private _disabled = false;
  private _required = false;
  private _state: FileInputState = 'empty';
  private _value: FileObject | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public canOpen(file: FileObject): boolean {
    // allow only when it exists in the backend
    return ['uploaded', 'verified'].includes(file.status);
  }

  public onDelete(): void {
    this.state = 'deleting';
    const countdown = this.deleteCountdown ?? 0;
    createCounter(countdown, this.counterKiller).subscribe((n) => {
      this.counter = n;
      this.changeDetectorRef.markForCheck();
    });
  }

  public onConfirmDelete(): void {
    this.writeValue(null);
    this.fileChange.emit(null);
    this.state = 'empty';
    this.onChange(null);
  }

  public onCancelDelete(): void {
    this.state = this.value.status ?? 'not-uploaded';
    this.counterKiller.next(null);
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this.counterKiller.complete();
  }

  public setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('.form-file-input-container');
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  public onContainerClick(): void {
    // do nothing, required by interface
  }

  public writeValue(file: FileObject | null): void {
    this.value = file;

    if (file?.status) {
      this.state = file?.status;
    }
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInput(file: File): void {
    this.value = FileObject.createFromFile(file);
    this.onChange(this.value);
    this.onTouched(this.value);
    this.fileChange.emit(this.value);
  }

  public onBlur(): void {
    this.onTouched(this.value);
  }

  public onFileOpen(file: FileObject): void {
    if (this.canOpen(file)) {
      this.fileOpen.emit(file);
    }
  }
}
